import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  orderBy,
  where,
  limit,
  startAfter,
  Timestamp,
  writeBatch,
  arrayUnion,
  arrayRemove,
  DocumentSnapshot
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Suggestion } from '../../types';

const SUGGESTIONS_COLLECTION = 'suggestions';
const SUGGESTIONS_PER_PAGE = 20;

interface SuggestionsQueryResult {
  suggestions: Suggestion[];
  hasMore: boolean;
  lastDoc?: DocumentSnapshot;
  total: number;
}

interface SuggestionsQueryOptions {
  pageSize?: number;
  lastDoc?: DocumentSnapshot;
  sortBy?: 'latest' | 'popular';
  status?: 'open' | 'under-review' | 'implemented' | 'declined' | 'all';
}

export const suggestionService = {
  // Get suggestions with pagination and filtering
  async getSuggestions(options: SuggestionsQueryOptions = {}): Promise<SuggestionsQueryResult> {
    try {
      const {
        pageSize = SUGGESTIONS_PER_PAGE,
        lastDoc,
        sortBy = 'latest',
        status = 'all'
      } = options;

      // Build the base query
      let q = query(collection(db, SUGGESTIONS_COLLECTION));

      // Add status filtering
      if (status !== 'all') {
        q = query(q, where('status', '==', status));
      }

      // Add sorting
      if (sortBy === 'latest') {
        q = query(q, orderBy('createdAt', 'desc'));
      } else if (sortBy === 'popular') {
        // Simple sort by upvoteCount only (no composite index needed)
        q = query(q, orderBy('upvoteCount', 'desc'));
      }
      
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      
      q = query(q, limit(pageSize + 1)); // +1 to check if there are more

      const querySnapshot = await getDocs(q);
      const docs = querySnapshot.docs;
      
      // Check if there are more documents
      const hasMore = docs.length > pageSize;
      const suggestionsToReturn = hasMore ? docs.slice(0, pageSize) : docs;
      
      const suggestions = suggestionsToReturn.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        adminResponseAt: doc.data().adminResponseAt?.toDate() || undefined,
      })) as Suggestion[];

      // Filter out deleted suggestions at the JavaScript level
      const filteredSuggestions = suggestions.filter(suggestion => !suggestion.deleted);

      return {
        suggestions: filteredSuggestions,
        hasMore: hasMore && filteredSuggestions.length === pageSize,
        lastDoc: hasMore ? suggestionsToReturn[suggestionsToReturn.length - 1] : undefined,
        total: filteredSuggestions.length // This is approximate when paginated
      };
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      throw error;
    }
  },

  // Add a new suggestion
  async addSuggestion(suggestionData: Omit<Suggestion, 'id' | 'createdAt' | 'updatedAt' | 'upvotes' | 'upvoteCount' | 'status'>): Promise<string> {
    try {
      // Filter out undefined values to avoid Firestore errors
      const cleanedData = Object.fromEntries(
        Object.entries(suggestionData).filter(([_, value]) => value !== undefined)
      );

      const docRef = await addDoc(collection(db, SUGGESTIONS_COLLECTION), {
        ...cleanedData,
        upvotes: [],
        upvoteCount: 0,
        status: 'open',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding suggestion:', error);
      throw error;
    }
  },

  // Toggle upvote on a suggestion
  async toggleUpvote(suggestionId: string, userEmail: string): Promise<{ isUpvoted: boolean; newCount: number }> {
    try {
      const suggestionRef = doc(db, SUGGESTIONS_COLLECTION, suggestionId);
      
      // Get current suggestion to check if user already upvoted
      const suggestionDoc = await getDocs(
        query(collection(db, SUGGESTIONS_COLLECTION), where('__name__', '==', suggestionId))
      );
      
      if (suggestionDoc.empty) {
        throw new Error('Suggestion not found');
      }
      
      const currentData = suggestionDoc.docs[0].data() as Suggestion;
      const hasUpvoted = currentData.upvotes?.includes(userEmail) || false;
      
      const batch = writeBatch(db);
      let result: { isUpvoted: boolean; newCount: number };
      
      if (hasUpvoted) {
        // Remove upvote
        const newCount = Math.max(0, (currentData.upvoteCount || 0) - 1);
        batch.update(suggestionRef, {
          upvotes: arrayRemove(userEmail),
          upvoteCount: newCount,
          updatedAt: Timestamp.now()
        });
        result = { isUpvoted: false, newCount };
      } else {
        // Add upvote
        const newCount = (currentData.upvoteCount || 0) + 1;
        batch.update(suggestionRef, {
          upvotes: arrayUnion(userEmail),
          upvoteCount: newCount,
          updatedAt: Timestamp.now()
        });
        result = { isUpvoted: true, newCount };
      }
      
      // Commit the batch BEFORE returning
      await batch.commit();
      return result;
    } catch (error) {
      console.error('Error toggling upvote:', error);
      throw error;
    }
  },

  // Update suggestion content (creator or admin only)
  async updateSuggestion(
    suggestionId: string, 
    updates: { title?: string; content?: string; fileUrl?: string; fileName?: string; fileSize?: number }
  ): Promise<void> {
    try {
      const suggestionRef = doc(db, SUGGESTIONS_COLLECTION, suggestionId);
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now()
      };
      
      await updateDoc(suggestionRef, updateData);
    } catch (error) {
      console.error('Error updating suggestion:', error);
      throw error;
    }
  },

  // Update suggestion status (admin only)
  async updateSuggestionStatus(
    suggestionId: string, 
    status: Suggestion['status'], 
    response?: string, 
    adminEmail?: string
  ): Promise<void> {
    try {
      const suggestionRef = doc(db, SUGGESTIONS_COLLECTION, suggestionId);
      const updateData: any = {
        status,
        updatedAt: Timestamp.now()
      };
      
      if (response && adminEmail) {
        updateData.adminResponse = response;
        updateData.adminResponseBy = adminEmail;
        updateData.adminResponseAt = Timestamp.now();
      }
      
      await updateDoc(suggestionRef, updateData);
    } catch (error) {
      console.error('Error updating suggestion status:', error);
      throw error;
    }
  },

  // Soft delete a suggestion (admin only)
  async deleteSuggestion(suggestionId: string, adminEmail: string): Promise<void> {
    try {
      const suggestionRef = doc(db, SUGGESTIONS_COLLECTION, suggestionId);
      await updateDoc(suggestionRef, {
        deleted: true,
        deletedBy: adminEmail,
        deletedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error deleting suggestion:', error);
      throw error;
    }
  },

  // Restore a deleted suggestion (admin only)
  async restoreSuggestion(suggestionId: string): Promise<void> {
    try {
      const suggestionRef = doc(db, SUGGESTIONS_COLLECTION, suggestionId);
      await updateDoc(suggestionRef, {
        deleted: false,
        deletedBy: null,
        deletedAt: null,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error restoring suggestion:', error);
      throw error;
    }
  },

  // Get suggestion counts by status
  async getStatusCounts(): Promise<{ open: number; underReview: number; implemented: number; declined: number }> {
    try {
      const [openSnapshot, reviewSnapshot, implementedSnapshot, declinedSnapshot] = await Promise.all([
        getDocs(query(collection(db, SUGGESTIONS_COLLECTION), where('status', '==', 'open'))),
        getDocs(query(collection(db, SUGGESTIONS_COLLECTION), where('status', '==', 'under-review'))),
        getDocs(query(collection(db, SUGGESTIONS_COLLECTION), where('status', '==', 'implemented'))),
        getDocs(query(collection(db, SUGGESTIONS_COLLECTION), where('status', '==', 'declined')))
      ]);

      return {
        open: openSnapshot.size,
        underReview: reviewSnapshot.size,
        implemented: implementedSnapshot.size,
        declined: declinedSnapshot.size
      };
    } catch (error) {
      console.error('Error fetching status counts:', error);
      return { open: 0, underReview: 0, implemented: 0, declined: 0 };
    }
  },

  // Check if user has upvoted a suggestion
  hasUserUpvoted(suggestion: Suggestion, userEmail: string): boolean {
    return suggestion.upvotes?.includes(userEmail) || false;
  },

  // Get user's suggestions
  async getUserSuggestions(userEmail: string): Promise<Suggestion[]> {
    try {
      const q = query(
        collection(db, SUGGESTIONS_COLLECTION),
        where('createdBy', '==', userEmail),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        adminResponseAt: doc.data().adminResponseAt?.toDate() || undefined,
      })) as Suggestion[];
    } catch (error) {
      console.error('Error fetching user suggestions:', error);
      return [];
    }
  }
};